import { atom } from 'recoil';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';

export type Tools =
    | 'pan'
    | 'boxZoom'
    | 'brush'
    | 'line'
    | 'registration'
    | 'transform'
    | 'lineDeform'
    | 'deform'
    | 'poi-move';

export type Operations =
    | 'clear-slice'
    | 'interpolation'
    | 'margin'
    | 'smoothing'
    | 'grid-layout-resizing'
    | 'boolean-operation';

export const defaultMainViewWidth = 61.8; // the width is defined in % of the layout container width

export const minSliceInterval = 2; // Minimum value for keeping every nth slice

export interface VoiBounds {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
}

export const selectedToolState = atom<Tools>({
    key: 'selectedToolState',
    default: 'pan',
});

export const zoomToCursorState = atom<boolean>({
    key: 'zoomToCursorState',
    default: true,
});

export const selectedOperationState = atom<Operations | null>({
    key: 'selectedOperationState',
    default: null,
});

export const mainViewWidthState = atom<number>({
    key: 'mainViewWidthState',
    default: defaultMainViewWidth,
});

export type BrushOptions = {
    diameter: number;
    mode: 'static' | 'smart';
    erase: boolean;
};

export const brushOptionsState = atom<BrushOptions>({
    key: 'brushOptionsState',
    default: {
        diameter: 20,
        mode: 'static',
        erase: false,
    },
});

export const contourClearanceModeState = atom<'current' | 'all'>({
    key: 'contourClearanceModeState',
    default: 'current',
});

export type LineOptions = {
    width: number;
    color: string;
    mode: 'draw' | 'erase' | null;
};

export const lineOptionsState = atom<LineOptions>({
    key: 'lineOptionsState',
    default: {
        width: 2,
        color: '#050505',
        mode: null,
    },
});

export type LineDeformOptions = {
    spreadPercent: number; // percentage of contour length, range [1, 100]
};

export const lineDeformOptionsState = atom<LineDeformOptions>({
    key: 'lineDeformOptionsState',
    default: {
        spreadPercent: 30,
    },
});

export type DeformOptions = {
    diameter: number; // mm, range [2, 50]
};

export const deformOptionsState = atom<DeformOptions>({
    key: 'deformOptionsState',
    default: {
        diameter: 20,
    },
});

export const keepEveryNthSliceState = atom<boolean>({
    key: 'keepEveryNthSliceState',
    default: false,
});

export const keepEveryNthSliceValueState = atom<number>({
    key: 'keepEveryNthSliceValueState',
    default: minSliceInterval,
});

export type RegistrationToolOptions = {
    mode: RegistrationMode | null;
};

export const registrationModeState = atom<RegistrationToolOptions>({
    key: 'registrationModeState',
    default: { mode: null },
});

export const voiCheckboxState = atom<boolean>({
    key: 'voiCheckboxState',
    default: false,
});

export const autoRegistrationInProgressState = atom<boolean>({
    key: 'autoRegistrationInProgressState',
    default: false,
});

export const voiBoundsState = atom<VoiBounds | null>({
    key: 'voiBoundsState',
    default: null,
});

export const scaleDisplayEnabledState = atom<boolean>({
    key: 'scaleDisplayEnabledState',
    default: false,
});

export type FloatingPanelId =
    | 'pan'
    | 'static-brush'
    | 'adaptive-brush'
    | 'line'
    | 'margin'
    | 'clear'
    | 'interpolation'
    | 'smoothing'
    | 'remove-small-parts'
    | 'fill-cavities'
    | null;

export const openFloatingPanelState = atom<FloatingPanelId>({
    key: 'openFloatingPanelState',
    default: null,
});

export type OperationPanelState =
    | { type: 'closed' }
    | { type: 'boolean-operation' }
    | { type: 'margin'; mode: 'symmetric' | 'asymmetric' }
    | { type: 'ring' };

export const operationPanelState = atom<OperationPanelState>({
    key: 'operationPanelState',
    default: { type: 'closed' },
});

/** When true the user can drag POIs in the viewport overlay. */
export const poiMoveActiveState = atom<boolean>({
    key: 'poiMoveActiveState',
    default: false,
});

/** When true, transforming a SUPPORT structure moves all SUPPORT structures together. */
export const transformAllSupportState = atom<boolean>({
    key: 'transformAllSupportState',
    default: true,
});
